import {
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
	parseDate,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import {
	Alert,
	Badge,
	Box,
	Button,
	Card,
	Col,
	Component,
	Divider,
	Dropdown,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Select,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Table,
	Tabs,
	TabsList,
	Text,
	Time,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	DatePickerField,
	FieldArray,
	Form,
	getEditablePhoneNumber,
	GetFieldValue,
	isTruthy,
	PhoneInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import React, {
	useCallback,
	useMemo,
	memo,
	useState,
	useEffect,
	useRef,
} from "react"
import * as Validator from "yup"
import {
	deleteDocument,
	TRemoveDocumentPayload,
	IGroupedBookingsResponse,
	TBookingTourists,
	TBookingType,
	TouristXHR,
	TTourist,
	TTouristDocument,
	TTouristDocumentType,
	TTouristGroup,
	useTourist,
} from "./store"
import useSWR, { mutate } from "swr"
import {
	AddressInputField,
	TAddressInputFieldValue,
	transformAddressToEditableValue,
	transformAddressToStorableValue,
} from "../Addresses"
import { ICabSchedule } from "../CabScheduling"
import { IBooking } from "../HotelBookings"
import { ILocation } from "../Locations"
import { TTravelActiviytBooking } from "../TravelActivityBookings"
import {
	ExistingTouristProfiles,
	genrateGuestFormLink,
	ITrip,
	TouristProfilesTableView,
} from "../Trips"
import { SelectWorldCountries } from "../World"
import { SelectTouristDocumentType, SelectTouristsInGroup } from "./List"
import pluralize from "pluralize"
import { DocumentFileInputField, IDocType } from "../DocTypes"
import { Email, PhoneNumber } from "../Contacts"
import { EditTouristProfileDialog, TTouristProfile } from "../TouristProfiles"
import { AddTouristProfileDialog } from "./AddTouristProfile"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"

interface IUpdateTouristProps extends Omit<
	React.ComponentProps<typeof TripTouristsManagementDialog>,
	"onSubmit" | "initialValues"
> {
	onSuccess?: (tourist: TTourist) => void
}

function AddTourist({
	onSuccess,
	tripId,
	tripStartDateLocal,
	...props
}: Omit<IUpdateTouristProps, "tourists"> & {
	tripId: number
}) {
	const xhr = useXHR()
	const handleCreate = useCallback(
		async (data: ITouristFormData) => {
			const payload = data.tourists.map((t) => {
				const { ui, nationality, phone_numbers, type, ...rest } = t
				return {
					...rest,
					trip_id: t.trip_id,
					documents: (t.documents || []).map((d) => {
						const { id, document_type, ...rest } = d
						return {
							...rest,
							document_type_id: document_type?.id ?? undefined,
						}
					}),
					nationality_id: t.nationality ? t.nationality.id : undefined,
					dob: t.dob ? formatDate(t.dob, "YYYY-MM-DD") : undefined,
					phone_numbers: (phone_numbers || []).filter((p) => p.number),
					type: !t.id ? type : undefined,
				}
			})
			const resp = await xhr.post("/tourists/bulk-save", {
				data: payload,
			})
			onSuccess?.(resp.data.data)
		},
		[xhr, onSuccess]
	)
	return (
		<TripTouristsManagementDialog
			onSubmit={handleCreate}
			tripStartDateLocal={tripStartDateLocal}
			tripId={tripId}
			{...props}
		/>
	)
}

export function AddTouristInDialog({
	onSuccess,
	children,
	...props
}: Omit<React.ComponentProps<typeof AddTourist>, "onSuccess" | "onCancel"> & {
	onSuccess: (contact: TTourist) => void
	children: (props: { add: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ add: open })}
			<Dialog open={isOpen} onClose={close} title="Add Guest Details" xl>
				<Dialog.Body>
					<AddTourist
						{...props}
						onCancel={close}
						onSuccess={(data) => {
							close()
							showSnackbar("Guest details added")
							onSuccess?.(data)
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function UpdateTourist({
	onSuccess,
	onCancel,
	tripId,
	...props
}: IUpdateTouristProps) {
	const xhr = useXHR()

	const handleUpdate = useCallback(
		async (data: ITouristFormData) => {
			const payload = data.tourists.map((t) => {
				const {
					ui,
					type,
					nationality,
					phone_numbers,
					trip,
					booking_tourists,
					...rest
				} = t
				return {
					...rest,
					trip_id: t.trip_id,
					documents: t.documents?.map((d) => {
						const { document_type, id, ...rest } = d
						return {
							...rest,
							document_type_id: document_type?.id ?? undefined,
						}
					}),
					nationality_id: t.nationality?.id,
					dob: t.dob ? formatDate(t.dob, "YYYY-MM-DD") : undefined,
					phone_numbers: (phone_numbers || []).filter((p) => p.number),
					type: !t.id ? type : undefined,
				}
			})

			const resp = await xhr.post("/tourists/bulk-save", {
				data: payload,
			})
			onSuccess?.(resp.data.data)

			// The following `mutate` calls are made to ensure we see the updated changes
			// under the docs > guests > All guests and specific guest sections. This will refresh
			// both the list of all guests (with document status) and the specific guest's data
			// in the corresponding views after bulk update.
			mutate(
				(key) =>
					typeof key === "string" && key.startsWith(`/tourists?trip=${tripId}`)
			)
			mutate(
				(key) => typeof key === "string" && key.startsWith(`/tourists/tourist/`)
			)
		},
		[xhr, onSuccess, tripId]
	)
	return (
		<TripTouristsManagementDialog
			onSubmit={handleUpdate}
			onCancel={onCancel}
			tripId={tripId}
			{...props}
		/>
	)
}

export function UpdateTouristInDialog({
	onSuccess,
	children,
	...props
}: Omit<IUpdateTouristProps, "onSuccess" | "onCancel"> & {
	onSuccess: (contact: TTourist) => void
	children: (props: { update: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ update: open })}
			<Dialog
				open={isOpen}
				onClose={close}
				lg
				title={"Trip Tourists Management"}
			>
				<Dialog.Body paddingX={"2"} paddingY={"1"}>
					<UpdateTourist
						{...props}
						onCancel={close}
						onSuccess={(data) => {
							close()
							showSnackbar("Guest details updated")
							onSuccess?.(data)
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export interface ITouristsGroupFormData {
	name: string
	members: Array<{
		is_primary: 0 | 1 | "0" | "1" | boolean
		tourist: TTourist | ITouristFormData["tourists"][number]
		id: number
	}>
}

export interface ITouristFormData {
	tourists: Array<{
		id: number | null
		trip_id: number
		name: string
		salutation?: TTourist["salutation"]
		gender?: TTourist["gender"]
		type?: "adult" | "child"
		dob?: Date
		age_during_trip?: number
		nationality?: ILocation["country"]
		email?: string
		number?: number
		country_code?: string
		is_primary?: number
		remarks?: string
		comments?: string
		phone_numbers?: Array<{
			country_code: string
			phone_number: string
			number: string
			is_primary?: boolean
		}>
		address?: TAddressInputFieldValue
		documents: TTouristDocument[]
		trip?: ITrip
		booking_tourists?: TBookingTourists[]
		ui?: {
			isExpanded: boolean
			showDocuments: boolean
			showAddress: boolean
			showRemarks: boolean
		}
		profile_id?: number
		profile?: TTouristProfile
	}>
}

const touristValidation = Validator.object().shape({
	name: Validator.string().required("Name is required"),
	type: Validator.string()
		.oneOf(["adult", "child"])
		.required("Tourist type is required"),
	salutation: Validator.mixed().nullable(),
	gender: Validator.string().nullable(),
	dob: Validator.date().nullable(),
	age_during_trip: Validator.number().when("type", {
		is: (type: ITouristFormData["tourists"][number]["type"]) =>
			type === "child",
		then: (schema) =>
			schema
				.integer()
				.typeError("Age is required for child")
				.required("Age is required for child"),
		otherwise: (schema) => schema.integer().nullable(),
	}),
	nationality: Validator.mixed().nullable(),
	email: Validator.string().nullable(),
	is_primary: Validator.boolean().nullable(),
	remarks: Validator.string().nullable(),
	phone_numbers: Validator.array().of(Validator.mixed()).nullable(),
	address: Validator.mixed().nullable(),
	documents: Validator.array()
		.of(
			Validator.object().shape({
				id: Validator.number().nullable(),
				description: Validator.string().nullable(),
				uid: Validator.string().required("Document ID is required"),
				document_type: Validator.mixed().required("Document type is required"),
			})
		)
		.nullable(),
})

const groupValidationSchema = validateFormValues(
	Validator.object().shape({
		name: Validator.string()
			.required("Please provide a name for this Group")
			.max(191, "Please use fewer characters."),
		tourists: Validator.array()
			.of(Validator.mixed())
			.min(1, "Please select/add at least one member to this Group."),
	})
)

function TouristListView({
	onSubmit,
	onCancel,
	tripStartDateLocal,
	tripId,
}: {
	onSubmit: (data: ITouristFormData) => Promise<void>
	onCancel?: () => void
	tripStartDateLocal?: string
	tripId: number
}) {
	const xhr = useXHR()
	const { tourists, isLoading, isValidating, error, mutate } = useTourist({
		tripId,
	})
	const initialValues = useMemo<ITouristFormData>(() => {
		return {
			tourists:
				(tourists?.length ?? 0) > 0
					? (tourists ?? []).map((t) => {
							const {
								dob,
								is_primary,
								phone_numbers,
								address,
								trip_id,
								name_without_salutation,
								...rest
							} = t

							return {
								...rest,
								name: name_without_salutation,
								dob: dob ? parseDate(dob) : undefined,
								is_primary: isTruthy(is_primary) ? 1 : 0,
								phone_numbers: phone_numbers.map((p) => ({
									...p,
									...getEditablePhoneNumber(p.phone_number, p.country_code),
								})),
								address: transformAddressToEditableValue(address),
								ui: {
									isExpanded: false,
									showDocuments: isTruthy(t?.documents),
									showAddress: Boolean(address?.location),
									showRemarks: Boolean(t?.remarks || t?.comments),
								},
								trip_id,
							}
						})
					: [
							{
								id: null,
								trip_id: tripId,
								name: "",
								salutation: undefined,
								type: "adult",
								dob: undefined,
								age_during_trip: undefined,
								nationality: undefined,
								email: undefined,
								number: undefined,
								country_code: undefined,
								is_primary: 1,
								remarks: undefined,
								phone_numbers: [],
								address: undefined,
								documents: [],
								ui: {
									isExpanded: true,
									showDocuments: false,
									showAddress: false,
									showRemarks: false,
								},
							},
						],
		}
	}, [tourists, tripId])

	if (isLoading || isValidating) return <Spinner alignCenter padding="4" />
	if (error) return <Alert status="error">{error.message}</Alert>

	return (
		<Stack gap="2" paddingX="2" paddingY="4">
			<Inline
				justifyContent={"between"}
				alignItems={"center"}
				width={"full"}
				flexWrap={"wrap"}
				gap={"2"}
			>
				<Inline gap="2">
					<Text fontSize="sm" color="muted" fontWeight="semibold">
						Showing {pluralize("tourist", tourists?.length ?? 0, true)}
					</Text>
					<Icons.Refresh
						cursor="pointer"
						spin={isLoading}
						color="muted"
						onClick={() => {
							mutate()
						}}
					/>
				</Inline>

				<CopyToClipboard>
					{({ copyTextToClipboard }) => (
						<Box display="flex" alignItems="center">
							<CopyToClipboardButton
								onClick={async () => {
									const { link } = await genrateGuestFormLink(xhr, tripId)
									await copyTextToClipboard(link)
									return true
								}}
								inline
							>
								<Button>
									<Icons.ClipboardCopy /> Request Documents
								</Button>
							</CopyToClipboardButton>
						</Box>
					)}
				</CopyToClipboard>
			</Inline>
			<TouristForm
				initialValues={initialValues}
				tripId={tripId}
				onSubmit={onSubmit}
				onCancel={onCancel}
				tripStartDateLocal={tripStartDateLocal}
			/>
		</Stack>
	)
}

function TouristForm({
	onSubmit,
	onCancel,
	tripStartDateLocal,
	tripId,
	initialValues,
}: {
	onSubmit: (data: ITouristFormData) => Promise<void>
	onCancel?: () => void
	tripStartDateLocal?: string
	tripId: number
	initialValues: ITouristFormData
}) {
	return (
		<Form<ITouristFormData>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					tourists: Validator.array()
						.of(touristValidation)
						.min(1, "Add at least one tourist")
						.required("Tourists are required"),
				})
			)}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
			onSubmit={withServerErrors(async (values) => {
				const tourists: ITouristFormData["tourists"] = values.tourists.map(
					({ address, profile, ...t }) => ({
						...t,
						address: address?.location
							? {
									...transformAddressToStorableValue(address),
									addressing_name: t.name,
									billing_details: address.billing_details,
								}
							: undefined,
					})
				)
				await onSubmit({ tourists })
			})}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<FieldArray<ITouristFormData["tourists"][number]> name="tourists">
						{({ fields }) => (
							<Stack gap="2" padding={"2"}>
								{fields.map((name, idx) => {
									return (
										<TouristCard
											key={name}
											name={name}
											onRemove={() => fields.remove(idx)}
											totalTourists={fields?.length || 0}
											idx={idx}
											tripId={tripId}
											tripStartDateLocal={tripStartDateLocal}
										/>
									)
								})}
								<Stack gap="2" paddingTop="3">
									<AddTouristButton
										onAdd={({ lastTouristIndex, lastTourist }) => {
											fields.push({
												id: null,
												trip_id: tripId,
												name: "",
												type: "adult",
												dob: undefined,
												age_during_trip: undefined,
												nationality:
													lastTourist && lastTourist.nationality
														? lastTourist.nationality
														: undefined,
												address: undefined,
												documents: [],
												is_primary: 0,
												phone_numbers: [],
												ui: {
													isExpanded: true,
													showDocuments: false,
													showAddress: false,
													showRemarks: false,
												},
											})

											if (lastTouristIndex) {
												form.change(
													`tourists.${lastTouristIndex}.ui.isExpanded` as never,
													false as never
												)
											}
										}}
									/>
									<Divider sm />
									<SubmissionError />
									<Inline gap="4" justifyContent="start">
										<Button type="submit" level="primary" disabled={submitting}>
											{submitting ? `Saving...` : `Save Tourists`}
										</Button>
										<Button onClick={onCancel} level="secondary">
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Stack>
						)}
					</FieldArray>
				</form>
			)}
		</Form>
	)
}

function TouristGroupForm({
	initialValues,
	tripId,
	group,
	onSubmit,
	onCancel,
}: {
	initialValues?: ITouristsGroupFormData
	tripId: number
	group?: TTouristGroup
	onSubmit: (group: ITouristsGroupFormData) => Promise<void>
	onCancel: () => void
}) {
	return (
		<Form<ITouristsGroupFormData>
			initialValues={
				initialValues || {
					name: "",
					members: [],
				}
			}
			validate={groupValidationSchema}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
		>
			{({ handleSubmit, submitting, form }) => (
				<form onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid paddingX="0">
							<Col sm={12} md={6} paddingLeft="0">
								<TextInputField
									name="name"
									label="Group Name"
									placeholder="e.g. Group Name"
									required
								/>
							</Col>

							<Col sm={12} md={6} paddingRight="0">
								<SelectField
									name="members"
									label="Assign Tourists"
									select={SelectTouristsInGroup}
									multiple
									placeholder="Select tourists..."
									fetchOnMount
									tripId={tripId}
									group={group}
								/>
							</Col>
						</Grid>

						<GetFieldValue<ITouristsGroupFormData["members"]> name="members">
							{({ value: members }) => {
								return (
									<Stack gap="1">
										<Inline justifyContent="between" marginBottom="2">
											<Text fontWeight="semibold" fontSize="base">
												Selected Guest
											</Text>
											<Text fontSize={"sm"} color={"muted"}>
												Total:{" "}
												{members.reduce((acc, m) => {
													if (m.tourist.type === "adult") {
														return acc + 1
													}
													return acc
												}, 0)}{" "}
												Adults,{" "}
												{members.reduce((acc, m) => {
													if (m.tourist.type === "child") {
														return acc + 1
													}
													return acc
												}, 0)}{" "}
												Children
											</Text>
										</Inline>
										<Box
											overflowY="auto"
											style={{
												maxHeight: "250px",
											}}
											borderColor="transparent"
											borderWidth="0"
											paddingX="0"
											paddingY="0"
											overflow="hidden"
											backgroundColor="transparent"
										>
											<Stack gap="2">
												{members.map((member, idx) => {
													const { tourist } = member
													return (
														<Box
															key={tourist.id}
															borderWidth="1"
															borderColor="default"
															rounded="md"
															paddingY="2"
															paddingX="3"
															bgColor="subtle"
															style={{
																transition: "background 0.18s",
															}}
														>
															<Inline
																justifyContent="between"
																alignItems="center"
															>
																<Box>
																	<Inline gap="2" alignItems="center">
																		<Box
																			width="5"
																			height="5"
																			display="flex"
																			alignItems="center"
																			justifyContent="center"
																			backgroundColor="default"
																		>
																			{tourist.type === "child" ? (
																				<Icons.Child size="6" />
																			) : (
																				<Icons.Adult size="6" />
																			)}
																		</Box>
																		<Stack>
																			<Inline gap="1" alignItems="center">
																				<Text fontWeight="semibold">
																					{tourist.name}
																				</Text>
																			</Inline>

																			<Inline alignItems="center">
																				{tourist.type === "child" &&
																				tourist.age_during_trip ? (
																					<Text color="muted" fontSize="sm">
																						{tourist.age_during_trip}y
																					</Text>
																				) : null}
																			</Inline>
																		</Stack>
																	</Inline>
																</Box>
																{tourist.type === "adult" ? (
																	<Button
																		size="sm"
																		title={
																			member.is_primary
																				? "Primary Contact"
																				: "Click to Make Primary Contact"
																		}
																		onClick={() => {
																			const updatedMembers = members.map(
																				(m, i) => {
																					if (i === idx) {
																						return {
																							...m,
																							is_primary: member.is_primary
																								? 0
																								: 1,
																						}
																					}
																					return {
																						...m,
																						is_primary: 0,
																					}
																				}
																			)

																			form.change(
																				"members" as never,
																				updatedMembers as never
																			)
																		}}
																		style={{ minWidth: 36 }}
																	>
																		{member.is_primary ? (
																			<Icons.FlagSolid color="primary" />
																		) : (
																			<Icons.Flag />
																		)}
																	</Button>
																) : null}
															</Inline>
														</Box>
													)
												})}
											</Stack>
										</Box>
									</Stack>
								)
							}}
						</GetFieldValue>

						<SubmissionError />

						<BookingTourist
							title="Tourist Group present in bookings"
							status="info"
							bookingData={group?.booking_tourist_members}
							warningText="The tourist group is part of the above bookings, editing/deleting will affect these bookings."
						/>
						<Inline gap="4" justifyContent="end">
							<Button level="secondary" type="button" onClick={onCancel}>
								Cancel
							</Button>
							<Button level="primary" type="submit" disabled={submitting}>
								Save
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function CreateTouristGroupInModal({
	tripId,
	onSuccess,
	children,
}: {
	tripId: number
	onSuccess: () => void
	children: (props: { manage: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{children({ manage: open })}
			<Dialog open={isOpen} onClose={close} title="Create Group">
				<Dialog.Body>
					<TouristGroupForm
						initialValues={undefined}
						tripId={tripId}
						onSubmit={async (data) => {
							await xhr.post<{ data: TTouristGroup }>(`/tourist-groups`, {
								trip_id: tripId,
								name: data.name,
								members: data.members.map((m) => {
									return {
										tourist_id: m.tourist.id,
										is_primary: m.is_primary,
									}
								}),
							})
							showSnackbar("Group created")
							onSuccess()
							close()
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function UpdateTouristGroupInModal({
	tripId,
	group,
	onSuccess,
	children,
}: {
	group: TTouristGroup
	tourists?: TTourist[] | undefined
	tripId: number
	onSuccess: () => void
	children: (props: { manage: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	const xhr = useXHR()
	if (!group) return null
	return (
		<>
			{children({ manage: open })}
			<Dialog open={isOpen} onClose={close} title="Edit Group">
				<Dialog.Body>
					<TouristGroupForm
						initialValues={{
							name: group.name,
							members: group?.members.map((m) => ({
								tourist: m.tourist,
								is_primary: m.is_primary ? 1 : 0,
								id: m.tourist.id,
								name: m.tourist.name,
							})),
						}}
						group={group}
						tripId={tripId}
						onSubmit={async (data) => {
							await xhr.patch<{ data: TTouristGroup }>(
								`/tourist-groups/${group.id}`,
								{
									name: data.name,
									members: data.members.map((m) => {
										return {
											tourist_id: m.tourist.id,
											is_primary: m.is_primary,
										}
									}),
								}
							)
							showSnackbar("Group updated")
							onSuccess()
							close()
						}}
						onCancel={close}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function TouristGroupListView({ tripId }: { tripId: number }) {
	const xhr = useXHR()
	const {
		data: touristGroups,
		isLoading,
		isValidating,
		mutate: revalidate,
	} = useSWR<TTouristGroup[]>(`/tourist-groups?trip=${tripId}`, () =>
		xhr
			.get(`/tourist-groups`, {
				params: {
					trip: tripId,
					limit: 1000,
					include: "bookings",
				},
			})
			.then((resp) => resp.data.data)
	)

	if (isLoading) {
		return <Spinner padding="4" alignCenter />
	}
	return (
		<Stack gap="2" padding="2">
			<Inline gap="2" justifyContent="between" alignItems="center" paddingY="2">
				<Inline gap="2">
					<Text fontSize="sm" color="muted" fontWeight="semibold">
						Showing {pluralize("group", touristGroups?.length ?? 0, true)}
					</Text>
					<Icons.Refresh
						cursor="pointer"
						spin={isValidating || isLoading}
						color="muted"
						onClick={() => {
							revalidate()
						}}
					/>
				</Inline>
				{(touristGroups?.length ?? 0) < 1000 ? (
					<CreateTouristGroupInModal
						tripId={tripId}
						onSuccess={() => {
							revalidate()
						}}
					>
						{({ manage }) => (
							<Button level="secondary" size="sm" onClick={manage}>
								<Icons.Plus /> Create Group
							</Button>
						)}
					</CreateTouristGroupInModal>
				) : null}
			</Inline>
			{touristGroups?.length ? (
				<Table
					bordered
					responsive
					striped
					headers={["Name", "Members", ""]}
					alignCols={{
						3: "right",
					}}
					rows={(touristGroups || []).map((group) => [
						<Box>
							<Inline gap="2">
								<Box
									width="8"
									height="8"
									rounded="full"
									backgroundColor="success"
									display="flex"
									alignItems="center"
									justifyContent="center"
								>
									<Icons.UserGroup color={"success"} />
								</Box>

								<Stack gap="1">
									<Text fontWeight="semibold">{group.name}</Text>
									<MembersAdultsChildrenCount members={group.members} />
								</Stack>
							</Inline>
						</Box>,
						<Box>
							<Inline gap="2" maxWidth="lg" flexWrap="wrap">
								{(group.members || []).map((m) => (
									<Badge
										key={m.tourist.id}
										status={
											isTruthy(m.is_primary)
												? "success"
												: m.tourist.type === "adult"
													? "primary"
													: "info"
										}
									>
										{m.tourist.name}
										{isTruthy(m.is_primary) ? (
											<Box as="span" marginLeft="1">
												<Icons.Star />
											</Box>
										) : null}
									</Badge>
								))}
							</Inline>
						</Box>,
						<Inline gap="2" justifyContent="end">
							<UpdateTouristGroupInModal
								tripId={tripId}
								group={group}
								onSuccess={() => {
									revalidate()
								}}
							>
								{({ manage }) => (
									<Button level="tertiary" size="sm" onClick={manage}>
										<Icons.Pencil />
									</Button>
								)}
							</UpdateTouristGroupInModal>
							<DeleteTouristGroup
								group={group}
								onRemove={async () => {
									const confirmMessage = `Delete group "${group.name}"? This cannot be undone.`
									if (window.confirm(confirmMessage)) {
										try {
											await xhr.delete(`/tourist-groups/${group.id}`)
											revalidate()
											showSnackbar(
												`The group "${group.name}" has been successfully deleted.`
											)
										} catch (e) {
											const error = e as Error
											window.alert(
												error.message ||
													"Failed to delete the group. Please try again."
											)
										}
									}
								}}
							>
								{({ onDelete }) => (
									<Button
										level="secondary"
										status="danger"
										size="sm"
										onClick={() => {
											onDelete()
										}}
									>
										<Icons.Trash />
									</Button>
								)}
							</DeleteTouristGroup>
						</Inline>,
					])}
				/>
			) : (
				<Box padding="8" textAlign="center">
					<Text color="muted" fontSize="base">
						No groups created yet. Create a group and add tourists to a group.
					</Text>
				</Box>
			)}
		</Stack>
	)
}

export interface ITouristOrGroupOption<TMemberType = TTourist | TTouristGroup> {
	id: string
	member_type: "tourists" | "tourist_groups"
	member_id: number
	name: string
	member: TMemberType
}

export function AddTouristDocumentDialog({
	open,
	onClose,
	onSuccess,
}: {
	open: boolean
	onClose: () => void
	onSuccess: (documentType: TTouristDocumentType) => void
}) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			lg
			title="Add a new document type for Guests"
		>
			<Dialog.Body>
				<NewTouristDocument onSuccess={onSuccess} onCancel={onClose} />
			</Dialog.Body>
		</Dialog>
	)
}

export function NewTouristDocument({
	onSuccess,
	onCancel,
	...props
}: {
	onSuccess: (documentType: TTouristDocumentType) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<NewTouristDocumentForm
			onSubmit={async (values) => {
				const { name } = values
				const resp = await xhr.post<{
					data: TTouristDocumentType
				}>("/document-types", {
					name,
				})
				const documentType: TTouristDocumentType = resp.data.data
				onSuccess(documentType)
			}}
			onCancel={onCancel}
			{...props}
		/>
	)
}

interface NewTouristDocumentFormData {
	name: string
}

const newTouristDocumentSchema = validateFormValues(
	Validator.object().shape({
		name: Validator.string()
			.required("Name for the document is required.")
			.max(100, "Document name must be less than 100 characters."),
	})
)

function NewTouristDocumentForm({
	onSubmit,
	onCancel,
	initialValues,
}: {
	onSubmit: (values: NewTouristDocumentFormData) => Promise<void>
	onCancel: () => void
	initialValues?: NewTouristDocumentFormData
}) {
	return (
		<Form<NewTouristDocumentFormData>
			initialValues={
				initialValues || {
					name: "",
				}
			}
			validate={newTouristDocumentSchema}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col xs={12} sm={4}>
							<Stack gap="1">
								<Heading as="h4">Document Name</Heading>
								<Text color="muted">
									Please provide a name for the document.
								</Text>
							</Stack>
						</Col>
						<Col>
							<Grid gap="4">
								<Col xs={12} sm={6}>
									<TextInputField
										label="Document Name"
										name="name"
										required
										placeholder="eg. Aadhaar Card"
										type="text"
									/>
								</Col>
							</Grid>
						</Col>
					</Grid>
					<Divider sm />
					<Grid>
						<Col xs={12} sm={{ offset: 4, span: 8 }}>
							<Stack gap="4">
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Saving.." : "Save Details"}
									</Button>
									{onCancel ? (
										<Button
											onClick={onCancel}
											level="tertiary"
											disabled={submitting}
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

export function DeleteTourist({
	children,
	tourist,
	onRemove,
}: {
	children: (props: { onDelete: () => void }) => React.ReactNode
	tourist: ITouristFormData["tourists"][number]
	onRemove: () => void
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({
				onDelete: () => {
					openDialog()
				},
			})}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`Delete Tourist: ${tourist.name}`}
			>
				<Dialog.Body>
					<Form
						initialValues={{ tourist }}
						onSubmit={() => {
							onRemove()
							closeDialog()
						}}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form onSubmit={handleSubmit} noValidate>
								<Stack gap="4">
									<BookingTourist bookingData={tourist.booking_tourists} />
									<Alert status="warning" title="Can NOT be undone!">
										This is an irreversable process and once deleted, a tourist
										can not be restored. Are you sure you want to delete this
										tourist ?
									</Alert>
									<SubmissionError />
									<Inline gap="4">
										<Button
											type="submit"
											disabled={submitting}
											status="warning"
										>
											I understand, delete this.
										</Button>
										<Button onClick={closeDialog} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function DeleteTouristGroup({
	children,
	group,
	onRemove,
}: {
	children: (props: { onDelete: () => void }) => React.ReactNode
	group: TTouristGroup
	onRemove: () => void
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	return (
		<>
			{children({
				onDelete: () => {
					openDialog()
				},
			})}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`Delete Tourist: ${group.name}`}
			>
				<Dialog.Body>
					<Form
						initialValues={group}
						onSubmit={withServerErrors(async (values) => {
							onRemove()
							closeDialog()
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form onSubmit={handleSubmit} noValidate>
								<Stack gap="4">
									<BookingTourist bookingData={group.booking_tourist_members} />
									<Alert status="warning" title="Can NOT be undone!">
										This is an irreversable process and once deleted, the group
										can not be restored. Are you sure you want to delete this
										group ?
									</Alert>
									<SubmissionError />
									<Inline gap="4">
										<Button
											type="submit"
											disabled={submitting}
											status="warning"
										>
											I understand, delete this.
										</Button>
										<Button onClick={closeDialog} disabled={submitting}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

/**
 * Groups an array of booking_tourists by their booking type for easy access and use in the UI.
 * - Attaches scheduled_cabs inside cab_schedules based on their cab_schedule_id.
 * - Removes the "scheduled_cabs" top-level group from the returned value.
 */
function groupBookingTouristsByType(
	booking_tourists?: TBookingTourists[]
): Omit<Record<TBookingType, TBookingTourists[]>, "scheduled_cabs"> {
	// Initialize the groups structure with empty arrays
	const EMPTY_RESULT = {
		hotel_bookings: [] as TBookingTourists<IBooking>[],
		cab_schedules: [] as TBookingTourists<ICabSchedule>[],
		travel_activity_bookings: [] as TBookingTourists<TTravelActiviytBooking>[],
	}

	if (!booking_tourists?.length) {
		return EMPTY_RESULT
	}

	const result: IGroupedBookingsResponse = {
		hotel_bookings: [],
		cab_schedules: [],
		travel_activity_bookings: [],
	}

	for (const bookingTourist of booking_tourists) {
		const type = bookingTourist.booking_type as TBookingType
		if (type === "cab_schedules") {
			// Enrich cab_schedules with an inner scheduled_cabs array for linking
			const enriched: TBookingTourists<ICabSchedule> = {
				...bookingTourist,
				booking: bookingTourist.booking as ICabSchedule,
			}
			result.cab_schedules.push(enriched)
		} else if (type === "hotel_bookings") {
			result.hotel_bookings.push(bookingTourist as TBookingTourists<IBooking>)
		} else if (type === "travel_activity_bookings") {
			result.travel_activity_bookings.push(
				bookingTourist as TBookingTourists<TTravelActiviytBooking>
			)
		}
	}
	return result
}

// Use BookingTouristTypes as a helper renderer for each group
function BookingTouristTypes({
	booking_type,
	bookings,
	status = "warning",
}: {
	booking_type: string
	bookings: TBookingTourists[]
	status?: React.ComponentProps<typeof Alert>["status"]
}) {
	if (bookings.length === 0) return null
	const renderBookingContent = () => {
		if (!bookings?.length) return null

		switch (booking_type) {
			case "hotel_bookings":
				return (
					<Stack as="ul" listStyleType="disc" paddingLeft="4" gap="2">
						{(bookings as IGroupedBookingsResponse["hotel_bookings"])
							.sort((a, b) => {
								const aDate = localOrUtcTimestampToLocalDate(
									a.booking.overall_checkin_local,
									a.booking.overall_checkin
								)
								const bDate = localOrUtcTimestampToLocalDate(
									b.booking.overall_checkin_local,
									b.booking.overall_checkin
								)
								return aDate.getTime() - bDate.getTime()
							})
							.map((b, i) => {
								const booking = b.booking
								const {
									hotel,
									overall_checkin,
									overall_checkin_local,
									booked,
									dropped,
								} = booking
								const { name } = hotel

								return (
									<li key={b.id}>
										<Inline
											gap="2"
											alignItems="center"
											justifyContent="start"
											fontSize="sm"
										>
											<Box>
												<Icons.Bed size="4" aria-label="Hotel" />
											</Box>
											<Time
												timestamp={overall_checkin}
												localTimestamp={overall_checkin_local}
												format="DD MMM"
												whiteSpace="nowrap"
											/>
											<Text
												as="span"
												fontWeight="semibold"
												key="name"
												style={{ marginLeft: 8 }}
											>
												{name}
											</Text>
											{dropped ? (
												<Badge danger size="sm">
													Dropped
												</Badge>
											) : booked ? (
												<Badge success size="sm">
													Booked
												</Badge>
											) : (
												<Badge warning size="sm">
													In Progress
												</Badge>
											)}
										</Inline>
									</li>
								)
							})}
					</Stack>
				)
			case "cab_schedules":
				return (
					<Stack as="ul" listStyleType="disc" paddingLeft="4" gap="2">
						{(bookings as IGroupedBookingsResponse["cab_schedules"])
							.sort((a, b) => {
								const aDate = localOrUtcTimestampToLocalDate(
									a.booking.start_date_local,
									a.booking.start_date
								)
								const bDate = localOrUtcTimestampToLocalDate(
									b.booking.start_date_local,
									b.booking.start_date
								)
								return aDate.getTime() - bDate.getTime()
							})
							.map((b, i) => {
								const { booking } = b
								const {
									start_date,
									start_date_local,
									start_time_local,
									transport_service,
									cabs_booked,
								} = booking
								const { name } = transport_service || {}
								const startDate = localOrUtcTimestampToLocalDate(
									start_date_local,
									start_date
								)
								return (
									<li key={b.id}>
										<Stack gap="2">
											<Inline
												gap="2"
												alignItems="start"
												justifyContent="start"
												fontSize="sm"
											>
												<Box>
													<Icons.Bus size="3" aria-label="Transport" />
												</Box>
												<Time
													value={startDate}
													format="DD MMM"
													whiteSpace="nowrap"
												/>
												<Stack gap="2">
													<Inline gap="2">
														{name ? (
															<Text as="span" fontWeight="semibold" key="name">
																{name}
															</Text>
														) : null}
														<Box>
															{cabs_booked ? (
																<Badge success size="sm">
																	Booked
																</Badge>
															) : (
																<Badge warning size="sm">
																	In Progress
																</Badge>
															)}
														</Box>
													</Inline>
													{start_time_local ? (
														<Inline alignItems="center" gap="1">
															<Icons.Clock size="3" />
															<Text>
																<b>Starts:</b>{" "}
																{formatDate(
																	start_time_local
																		? parseDate(start_time_local, "HH:mm:ss")
																		: utcTimestampToLocalDate(start_date),
																	"HH:mm [hrs]"
																)}
															</Text>
														</Inline>
													) : null}
												</Stack>
											</Inline>
										</Stack>
									</li>
								)
							})}
					</Stack>
				)
			case "travel_activity_bookings":
				return (
					<Stack as="ul" listStyleType="disc" paddingLeft="4" gap="2">
						{(bookings as IGroupedBookingsResponse["travel_activity_bookings"])
							.sort((a, b) => {
								const aDate = localOrUtcTimestampToLocalDate(
									a.booking.start_date_local,
									a.booking.start_date
								)
								const bDate = localOrUtcTimestampToLocalDate(
									b.booking.start_date_local,
									b.booking.start_date
								)
								return aDate.getTime() - bDate.getTime()
							})
							.map((b, i) => {
								const activityBooking = b.booking
								const {
									ticket_type,
									activity,
									start_date,
									start_date_local,
									end_time_formatted,
									start_time_formatted,
									booked,
								} = activityBooking
								const startDate = localOrUtcTimestampToLocalDate(
									start_date_local,
									start_date
								)
								const activityName = activity.name
								const ticketType = ticket_type?.name

								return (
									<li key={b.id}>
										<Inline
											gap="2"
											alignItems="start"
											justifyContent="start"
											fontSize="sm"
										>
											<Box>
												<Icons.Ticket size="3" aria-label="Activity" />
											</Box>

											{startDate ? (
												<Time
													key="dates"
													value={startDate}
													format="DD MMM"
													flexShrink="0"
												/>
											) : null}
											{activityName ? (
												<Stack gap="2">
													<Inline gap="2">
														<Text fontWeight="semibold" key="name">
															{activityName}
															{ticketType ? <> &ndash; {ticketType}</> : null}
														</Text>
														<Box>
															{booked ? (
																<Badge success size="sm">
																	Booked
																</Badge>
															) : (
																<Badge warning size="sm">
																	In Progress
																</Badge>
															)}
														</Box>
													</Inline>

													<Inline>
														{joinAttributes(
															start_time_formatted ? (
																<Inline alignItems="center" gap="1">
																	<Icons.Clock size="3" />
																	<Text fontSize="sm">
																		<b>Starts:</b> {start_time_formatted}
																	</Text>
																</Inline>
															) : null,
															end_time_formatted ? (
																<Inline alignItems="center" gap="1">
																	<Icons.Clock size="3" />
																	<Text fontSize="sm">
																		<b>Ends:</b> {end_time_formatted}
																	</Text>
																</Inline>
															) : null
														)}
													</Inline>
												</Stack>
											) : null}
										</Inline>
									</li>
								)
							})}
					</Stack>
				)
			default:
				return (
					<Stack gap="2">
						{bookings && bookings.length > 0 ? (
							bookings.map((b, i) => (
								<Text key={i} fontSize="sm">
									Booking #{b.booking_id || i + 1}
								</Text>
							))
						) : (
							<Text fontSize="sm" color="muted">
								No bookings available.
							</Text>
						)}
					</Stack>
				)
		}
	}

	const titles: Record<string, string> = {
		hotel_bookings: "Hotel Bookings",
		cab_schedules: "Transport Bookings",
		scheduled_cabs: "Transport Bookings",
		travel_activity_bookings: "Activity Bookings",
	}

	return (
		<Box>
			<Text fontWeight="semibold" fontSize="sm">
				{titles[booking_type] || booking_type}
			</Text>
			{renderBookingContent()}
		</Box>
	)
}

const BookingTourist = ({
	bookingData,
	title = "Tourist present in bookings",
	status = "warning",
	warningText = "The tourist is part of the above bookings, editing/deleting will affect these bookings.",
}: {
	title?: string
	bookingData: TBookingTourists[] | undefined
	status?: React.ComponentProps<typeof Alert>["status"]
	warningText?: string
}) => {
	if (!bookingData || bookingData?.length === 0) return null
	const grouped = groupBookingTouristsByType(bookingData)
	const typeKeys = Object.keys(grouped) as Array<keyof typeof grouped>
	if (!typeKeys.length) return null

	return (
		<Alert status={status} title={title}>
			<Stack gap="1">
				{typeKeys.map((booking_typeKey) => {
					const type = booking_typeKey
					const bookings = grouped[booking_typeKey]
					if (bookings.length === 0) {
						return null
					}
					return (
						<Box key={booking_typeKey}>
							<BookingTouristTypes
								booking_type={type}
								bookings={bookings}
								status={status}
							/>
							<Divider marginBottom="2" marginTop="2" />
						</Box>
					)
				})}
				<Text color="danger" fontSize="sm">
					{warningText}
				</Text>
			</Stack>
		</Alert>
	)
}

interface TouristExpandedProps {
	name: string
	tourist: ITouristFormData["tourists"][number]
	tripStartDateLocal?: string
	tripId: number
}

const adultSalutations = ["Mr.", "Mrs.", "Ms."]
const childSalutations = ["Master", "Miss"]

const TouristExpanded = memo(function TouristExpanded({
	name,
	tourist,
	tripStartDateLocal,
	tripId,
}: TouristExpandedProps) {
	const form = useForm<ITouristFormData, Partial<ITouristFormData>>()
	return (
		<Stack gap="2" paddingY="4">
			<TouristInfo
				name={name}
				tourist={tourist}
				tripId={tripId}
				tripStartDateLocal={tripStartDateLocal}
			/>
			<InternalCommentsAndRemarks name={name} />
			<TouristAddress name={name} tourist={tourist} />
			<TouristDocumentsList name={name} tripId={tripId} tourist={tourist} />
			<TouristActions
				name={name}
				tourist={tourist}
				tripStartDateLocal={tripStartDateLocal}
			/>
			<ExistingTouristProfiles exceptId={tripId} fieldPrefix={name}>
				{({
					touristProfiles,
					isLoading,
					selectedProfileId,
					profile,
					shouldFetch,
				}) => {
					const selectTouristProfile = (touristProfile: TTouristProfile) => {
						const {
							type,
							salutation,
							name_without_salutation,
							dob,
							nationality,
							phone_numbers,
							email,
							address,
							gst_number,
							remarks,
							id,
						} = touristProfile

						let calculatedAge: number | undefined = undefined
						if (dob && tripStartDateLocal) {
							try {
								calculatedAge = getDiff(
									parseDate(tripStartDateLocal),
									parseDate(dob),
									"years"
								)
							} catch (e) {
								calculatedAge = undefined
							}
						}

						form.change(
							name as never,
							{
								...tourist,
								type: type ?? "adult",
								salutation: salutation ?? undefined,
								name: name_without_salutation ?? "",
								dob: dob ? parseDate(dob) : undefined,
								age_during_trip: calculatedAge ?? undefined,
								nationality: nationality ?? undefined,
								phone_numbers: phone_numbers ?? [],
								email: email ?? undefined,
								address: address
									? transformAddressToEditableValue({
											...address,
											billing_details: gst_number,
										})
									: undefined,
								remarks: remarks ?? undefined,
								ui: {
									...tourist?.ui,
									showAddress: !!address?.location,
									showRemarks: !!remarks,
								},
								profile_id: id,
								profile: touristProfile,
							} as never
						)
					}
					const unSelectTouristProfile = () => {
						form.change(
							name as never,
							{
								...tourist,
								type: "adult",
								salutation: undefined,
								name: "",
								dob: undefined,
								age_during_trip: undefined,
								nationality: undefined,
								phone_numbers: [],
								email: "",
								address: undefined,
								comments: undefined,
								remarks: undefined,
								ui: {
									...tourist.ui,
									showAddress: false,
									showRemarks: false,
								},
								profile_id: null,
								profile: undefined,
							} as never
						)
					}

					const profilesToShow = touristProfiles
						? profile
							? [
									profile,
									...touristProfiles.data.filter((t) => t.id !== profile.id),
								]
							: touristProfiles.data
						: profile
							? [profile]
							: null

					return (
						<Stack
							paddingX="4"
							justifyContent="center"
							style={{ minHeight: "115px" }}
						>
							{isLoading ? (
								<Inline alignItems="center" justifyContent="center" gap="3">
									<Icons.Refresh
										cursor="pointer"
										spin={isLoading}
										color="muted"
									/>
									<Text color="muted">
										Checking for existing tourist profiles....
									</Text>
								</Inline>
							) : profilesToShow ? (
								<TouristProfilesTableView
									selectedProfileId={selectedProfileId}
									touristProfiles={profilesToShow}
									selectTouristProfile={selectTouristProfile}
									unSelectTouristProfile={unSelectTouristProfile}
								/>
							) : shouldFetch ? (
								<Alert status="warning">
									No existing tourist profiles were found for the provided
									details
								</Alert>
							) : (
								<Alert status="info">
									Enter a name, phone number or email address to check for
									existing tourist profiles.
								</Alert>
							)}
						</Stack>
					)
				}}
			</ExistingTouristProfiles>
		</Stack>
	)
})

interface TouristHeaderProps {
	name: string
	tourist: ITouristFormData["tourists"][number]
	onRemove: () => void
	idx: number
	totalTourists: number
}

const TouristHeader = memo(function TouristHeader({
	name,
	tourist,
	onRemove,
	idx,
	totalTourists,
}: TouristHeaderProps) {
	const form = useForm<ITouristFormData, Partial<ITouristFormData>>()
	return (
		<Inline
			alignItems="center"
			justifyContent="between"
			gap="2"
			padding="3"
			borderWidth="1"
			borderColor="transparent"
			rounded="md"
			bgColor={
				tourist.ui?.isExpanded
					? "primary"
					: { default: "default", hover: "primary" }
			}
			flexWrap="wrap"
		>
			<Box
				style={{
					flex: "1 1 auto",
				}}
				onClick={() => {
					form.change(
						`${name}.ui.isExpanded` as never,
						!(tourist.ui?.isExpanded || false) as never
					)
				}}
			>
				<Inline gap="2">
					<Text>{idx + 1}.</Text>
					<Box>
						{tourist?.type === "adult" ? (
							<Icons.UserRound color="primary" />
						) : (
							<Icons.Child color="default" />
						)}
					</Box>

					<Stack gap="2" width="full">
						<Inline gap="2" justifyContent="between" width="full">
							<Stack>
								<Inline gap="1">
									<Inline gap="1">
										<Text fontWeight="semibold" fontSize="md">
											{tourist?.salutation}
										</Text>
										<Text fontWeight="semibold" fontSize="md">
											{tourist?.name}
										</Text>
										<Text fontWeight="semibold" fontSize="md">
											{tourist?.age_during_trip
												? `(${tourist?.age_during_trip}y)`
												: null}
										</Text>
									</Inline>
									<Box>
										<Badge
											status={tourist?.type === "adult" ? "primary" : "info"}
											style={{ textTransform: "capitalize" }}
										>
											{tourist?.type}
										</Badge>
									</Box>
								</Inline>
								<Inline marginTop="1" gap="2" flexWrap={"wrap"}>
									{joinAttributes(
										tourist.phone_numbers?.length ? (
											<Stack gap="1">
												{tourist.phone_numbers
													?.filter((pn) => Boolean(pn.number))
													?.map((contact) => (
														<PhoneNumber value={contact.phone_number} />
													))}
											</Stack>
										) : null,
										tourist.email ? <Email value={tourist.email} /> : null
									)}
								</Inline>
							</Stack>
							<Stack gap="2">
								{(tourist?.comments?.length ?? 0) > 0 ? (
									<Tooltip
										interactive
										placement="bottom"
										content={tourist.comments}
									>
										<Inline marginX="2" gap="1" fontSize="sm">
											<Text>Internal Comments:</Text>
											<Text
												textOverflow="truncate"
												style={{
													maxWidth: "200px",
												}}
											>
												{tourist.comments}
											</Text>
										</Inline>
									</Tooltip>
								) : null}
								{(tourist?.remarks?.length ?? 0) > 0 ? (
									<Tooltip
										interactive
										placement="bottom"
										content={tourist.remarks}
									>
										<Inline marginX="2" gap="1" fontSize="sm">
											<Text color="success">Agent/Customer Remarks:</Text>
											<Text
												color="success"
												textOverflow="truncate"
												style={{
													maxWidth: "200px",
												}}
											>
												{tourist.remarks}
											</Text>
										</Inline>
									</Tooltip>
								) : null}
							</Stack>
						</Inline>
					</Stack>
				</Inline>
			</Box>
			<Inline
				gap="1"
				alignItems="center"
				style={{
					flexShrink: 0,
				}}
			>
				<TouristDocumentsSummaryButton documents={tourist.documents} />
				{tourist.type === "adult" ? (
					<Button
						size="sm"
						title={
							tourist.is_primary
								? `Primary Tourist: ${tourist.name}`
								: `Click to make ${tourist.name} Primary Tourist`
						}
						onClick={() => {
							const tourists = form.getState().values.tourists
							const updatedTourists = tourists.map((t, i) => ({
								...t,
								is_primary: i === idx ? 1 : 0,
							}))
							form.change("tourists", updatedTourists)
						}}
					>
						{tourist.is_primary ? (
							<Icons.FlagSolid color="primary" />
						) : (
							<Icons.Flag />
						)}
					</Button>
				) : null}
				<DeleteTourist tourist={tourist} onRemove={onRemove}>
					{({ onDelete }) => (
						<Button
							title={`Delete Tourist ${tourist.name}`}
							size="sm"
							aria-label="Delete"
							onClick={() => {
								const hasBookings = (tourist?.booking_tourists?.length ?? 0) > 0
								const isPrimary = isTruthy(tourist.is_primary)
								if (isPrimary) {
									window.alert(
										`You cannot delete the Primary Tourist "${tourist.name}". Please assign another tourist as primary before deleting.`
									)
									return
								} else if (hasBookings) {
									onDelete()
								} else {
									if (
										tourist.id &&
										!window.confirm(
											`Are you sure you want to delete "${tourist.name}"? This action cannot be undone and you have to re-enter this tourist's details if you need to add again.`
										)
									) {
										return
									}
									onRemove()
								}
							}}
							disabled={totalTourists === 1}
						>
							<Icons.Trash />
						</Button>
					)}
				</DeleteTourist>
				<GetFieldValue<boolean> name={`${name}.ui.isExpanded`}>
					{({ value: isExpanded }) => {
						return (
							<Button
								size="sm"
								level="secondary"
								onClick={() =>
									form.change(
										`${name}.ui.isExpanded` as never,
										!isExpanded as never
									)
								}
								aria-label={isExpanded ? "Collapse" : "Expand"}
							>
								{isExpanded ? (
									<Icons.ChevronDown
										style={{
											transform: "rotate(180deg)",
										}}
									/>
								) : (
									<Icons.ChevronDown />
								)}
							</Button>
						)
					}}
				</GetFieldValue>
			</Inline>
		</Inline>
	)
})

const TouristDocumentsSummaryButton = ({
	documents,
}: {
	documents: TTouristDocument[]
}) => {
	const filteredDocuments = Array.isArray(documents)
		? documents.filter((doc) => !!doc?.document_type)
		: []
	const total = filteredDocuments.length
	const uploaded = filteredDocuments.filter((doc) => doc.media_url).length
	return total > 0 ? (
		<Tooltip
			placement="bottom"
			interactive
			content={
				<Stack gap="1">
					{filteredDocuments.map((doc, i) => (
						<Text
							key={doc.uid || i}
							fontSize="sm"
							style={{
								whiteSpace: "nowrap",
							}}
						>
							<Inline gap="2" alignItems="center">
								{doc.media_url ? (
									<Icons.OkCircleSolid />
								) : (
									<Icons.DocumentText />
								)}
								<Text as="span" fontWeight="semibold">
									{doc.document_type?.name}
								</Text>
								{doc.uid && (
									<Text
										as="span"
										color="muted"
										fontSize="xs"
										style={{
											marginLeft: 4,
										}}
									>
										({doc.uid})
									</Text>
								)}
							</Inline>
						</Text>
					))}
				</Stack>
			}
		>
			<Button
				size="sm"
				aria-label={`View ${pluralize("document", total, true)}`}
				style={{
					display: "flex",
					alignItems: "center",
					gap: 4,
				}}
			>
				{uploaded === total ? <Icons.OkCircleSolid /> : <Icons.DocumentText />}
				<Text fontSize="xs" fontWeight="semibold">
					{uploaded}/{total}
				</Text>
			</Button>
		</Tooltip>
	) : null
}

interface TouristCardProps {
	name: string
	onRemove: () => void
	idx: number
	tripStartDateLocal?: string
	tripId: number
	totalTourists: number
}

const TouristCard = memo(function TouristCard({
	name,
	onRemove,
	idx,
	totalTourists,
	tripId,
	tripStartDateLocal,
}: TouristCardProps) {
	const { value: tourist } =
		useFieldValue<ITouristFormData["tourists"][number]>(name)
	return (
		<Box
			borderWidth="1"
			borderColor="muted"
			rounded="md"
			boxShadow="sm"
			cursor="pointer"
		>
			{/* Card Header: Summary + Actions */}
			<TouristHeader
				name={name}
				tourist={tourist}
				onRemove={onRemove}
				idx={idx}
				totalTourists={totalTourists}
			/>
			{/* Expanded Card */}
			<GetFieldValue<boolean> name={`${name}.ui.isExpanded`}>
				{({ value: isExpanded }) => {
					return isExpanded ? (
						<TouristExpanded
							name={name}
							tourist={tourist}
							tripStartDateLocal={tripStartDateLocal}
							tripId={tripId}
						/>
					) : null
				}}
			</GetFieldValue>
		</Box>
	)
})

interface AddTouristButtonProps {
	onAdd: ({
		lastTouristIndex,
		lastTourist,
	}: {
		lastTouristIndex: number
		lastTourist: ITouristFormData["tourists"][number] | undefined
	}) => void
}

const AddTouristButton = memo(function AddTouristButton({
	onAdd,
}: AddTouristButtonProps) {
	const { value: tourists } =
		useFieldValue<ITouristFormData["tourists"]>("tourists")
	return tourists.length < 1000 ? (
		<Inline justifyContent="start">
			<Button
				size="sm"
				onClick={() => {
					const lastTouristIndex =
						(tourists?.length ?? 0) > 0 ? tourists.length - 1 : 0
					const lastTourist =
						(tourists?.length ?? 0) > 0 ? tourists[lastTouristIndex] : undefined
					onAdd({ lastTouristIndex, lastTourist })
				}}
			>
				<Icons.Plus /> Add Tourist
			</Button>
		</Inline>
	) : null
})

function MembersAdultsChildrenCount({
	members,
}: {
	members: TTouristGroup["members"]
}) {
	if ((members?.length ?? 0) === 0) return null
	const childrenCount = members.filter((m) => m.tourist.type === "child").length
	const adultCount = members.filter((m) => m.tourist.type === "adult").length

	const parts = []
	if (adultCount > 0) {
		parts.push(`${adultCount}A`)
	}
	if (childrenCount > 0) {
		parts.push(`${childrenCount}C`)
	}
	return (
		<Text fontWeight="semibold" fontSize="sm">
			{parts.join(", ")}
		</Text>
	)
}

function TripTouristsManagementDialog({
	onSubmit,
	onCancel,
	tripStartDateLocal,
	tripId,
}: {
	onSubmit: (data: ITouristFormData) => Promise<void>
	onCancel?: () => void
	tripStartDateLocal?: string
	tripId: number
}) {
	return (
		<Box>
			<Tabs defaultActive="tourists" layout="col">
				<TabsList>
					<TabItem id="tourists">Tourists</TabItem>
					<TabItem id="groups">Tourist Groups</TabItem>
				</TabsList>
				<TabContent id="tourists">
					<TouristListView
						onSubmit={onSubmit}
						onCancel={onCancel}
						tripStartDateLocal={tripStartDateLocal}
						tripId={tripId}
					/>
				</TabContent>
				<TabContent id="groups" unmountOnInactive>
					<TouristGroupListView tripId={tripId} />
				</TabContent>
			</Tabs>
		</Box>
	)
}

function InternalCommentsAndRemarks({ name }: { name: string }) {
	const { value: showRemarks } = useFieldValue<boolean>(
		`${name}.ui.showRemarks`
	)
	if (!showRemarks) return null
	return (
		<Grid>
			<Col xs={12} sm={7} md={6} paddingY="2">
				<TextAreaInputField
					name={`${name}.comments`}
					label="Internal Comments (Not Shared)"
					placeholder="Additional notes about the tourist"
					maxLength={500}
				/>
			</Col>
			<Col xs={12} sm={5} md={6} paddingY="2">
				<TextAreaInputField
					name={`${name}.remarks`}
					label="Remarks for Agent/Customer"
					placeholder="Additional notes about the tourist"
					maxLength={1000}
				/>
			</Col>
		</Grid>
	)
}

function TouristAddress({
	name,
	tourist,
}: {
	name: string
	tourist: ITouristFormData["tourists"][number]
}) {
	const { value: showAddress } = useFieldValue<boolean>(
		`${name}.ui.showAddress`
	)
	const form = useForm()
	const handleBillingDetailsChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const value = e.currentTarget.value
			form.batch(() => {
				form.change(`${name}.address.billing_details` as never, value as never)
				form.change(
					`${name}.address.addressing_name` as never,
					tourist.name as never
				)
			})
		},
		[name, form, tourist.name]
	)

	if (!showAddress) return null

	return (
		<Grid>
			<Col xs={12} sm={7} md={6} paddingY="2">
				<AddressInputField
					name={`${name}.address`}
					label="Origin City/State"
					onlyLocation
					hidePreviewForLocation
				/>
			</Col>
			<Col xs={12} sm={5} md={6} paddingY="2">
				<TextAreaInputField
					name={`${name}.address.billing_details`}
					label="Guest Billing Details"
					placeholder="e.g. GST: 12GYR123KHS"
					onChange={handleBillingDetailsChange}
				/>
			</Col>
		</Grid>
	)
}

function TouristActions({
	name,
	tourist,
	tripStartDateLocal,
}: {
	name: string
	tourist: ITouristFormData["tourists"][number]
	tripStartDateLocal?: string
}) {
	const form = useForm<ITouristFormData, Partial<ITouristFormData>>()

	const toggleShowRemarks = useCallback(() => {
		form.change(
			`${name}.ui.showRemarks` as never,
			!tourist.ui?.showRemarks as never
		)
	}, [name, form, tourist.ui?.showRemarks])

	const toggleShowDocuments = useCallback(() => {
		form.change(
			`${name}.ui.showDocuments` as never,
			!tourist.ui?.showDocuments as never
		)
	}, [name, form, tourist.ui?.showDocuments])

	const toggleShowAddress = useCallback(() => {
		form.change(
			`${name}.ui.showAddress` as never,
			!tourist.ui?.showAddress as never
		)
	}, [name, form, tourist.ui?.showAddress])

	const { value: showRemarks } = useFieldValue(`${name}.ui.showRemarks`)
	const { value: showDocs } = useFieldValue(`${name}.ui.showDocuments`)
	const { value: showAddress } = useFieldValue(`${name}.ui.showAddress`)

	return (
		<Inline justifyContent="end" gap="2" flexWrap="wrap">
			{tourist.type === "adult" ? (
				tourist.profile ? (
					<EditTouristProfileDialog
						touristProfile={tourist.profile}
						onSuccess={(touristProfile) => {
							form.change(
								name as never,
								{
									...tourist,
									profile_id: touristProfile.id,
									profile: touristProfile,
								} as never
							)
						}}
					>
						{({ show }) => (
							<Button level="tertiary" size="sm" onClick={show}>
								<Icons.UserPencil />
								Edit Tourist Profile
							</Button>
						)}
					</EditTouristProfileDialog>
				) : (
					<AddTouristProfileDialog
						tourist={tourist}
						onSuccess={(touristProfile) => {
							const {
								type,
								salutation,
								name_without_salutation,
								dob,
								nationality,
								phone_numbers,
								email,
								address,
								gst_number,
								remarks,
								id,
							} = touristProfile

							let calculatedAge: number | undefined = undefined
							if (dob && tripStartDateLocal) {
								try {
									calculatedAge = getDiff(
										parseDate(tripStartDateLocal),
										parseDate(dob),
										"years"
									)
								} catch (e) {
									calculatedAge = undefined
								}
							}

							form.change(
								name as never,
								{
									...tourist,
									type: type ?? "adult",
									salutation: salutation ?? undefined,
									name: name_without_salutation ?? "",
									dob: dob ? parseDate(dob) : undefined,
									age_during_trip: calculatedAge,
									nationality: nationality ?? undefined,
									phone_numbers: phone_numbers ?? [],
									email: email ?? undefined,
									address: address
										? transformAddressToEditableValue({
												...address,
												billing_details: gst_number,
											})
										: undefined,
									remarks: remarks ?? undefined,
									ui: {
										...tourist.ui,
										showAddress: !!address?.location,
										showRemarks: !!remarks,
									},

									profile_id: id,
									profile: touristProfile,
								} as never
							)
						}}
					>
						{({ show }) => (
							<Button level="tertiary" size="sm" onClick={show}>
								<Icons.UserPlus />
								Add Tourist Profile
							</Button>
						)}
					</AddTouristProfileDialog>
				)
			) : null}
			<Button onClick={toggleShowRemarks} level="tertiary" size="sm">
				<Icons.Plus />
				{showRemarks ? "Hide Remarks/Comments" : "Add Remarks/Comments"}
			</Button>
			<FieldArray<ITouristFormData["tourists"][number]["documents"][number]>
				name={`${name}.documents`}
			>
				{({ fields: docFields }) => (
					<Button
						level="tertiary"
						size="sm"
						onClick={() => {
							if (docFields.length === 0) {
								docFields.push({
									id: null,
									uid: "",
									description: undefined,
									document_type: null,
								})
							}
							toggleShowDocuments()
						}}
					>
						<Icons.Document />
						{showDocs ? "Hide Documents" : "Add Documents"}
					</Button>
				)}
			</FieldArray>
			<Button level="tertiary" size="sm" onClick={toggleShowAddress}>
				<Icons.BankNotes />
				{showAddress ? "Hide Address/Billing" : "Add Address/Billing"}
			</Button>
		</Inline>
	)
}

function TouristInfo({
	name,
	tourist,
	tripId,
	tripStartDateLocal,
}: TouristExpandedProps) {
	const form = useForm<ITouristFormData, Partial<ITouristFormData>>()

	const handleTypeChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.currentTarget.value
			form.batch(() => {
				form.change(`${name}.type` as never, value as never)
				form.change(`${name}.salutation` as never, "" as never)
			})
		},
		[name, form]
	)

	const handleDobChange = useCallback(
		(dob: Date | undefined) => {
			form.change(`${name}.dob` as never, dob as never)
			if (dob && tripStartDateLocal) {
				let calculatedAge: number | undefined = undefined
				try {
					calculatedAge = getDiff(
						parseDate(tripStartDateLocal),
						parseDate(dob),
						"years"
					)
				} catch (e) {
					calculatedAge = undefined
				}
				form.change(`${name}.age_during_trip` as never, calculatedAge as never)
			} else {
				form.change(`${name}.age_during_trip` as never, undefined)
			}
		},
		[name, form, tripStartDateLocal]
	)
	return (
		//First Row: Type, Name, Nationality, DOB, Age, Salutation
		<Grid>
			<Col xs={12} sm={3} md={3} paddingY="2">
				<Stack gap="1">
					<SelectInputField
						maxWidth="xs"
						name={`${name}.type`}
						label="Type"
						disabled={!!tourist.id}
						required
						placeholder="Select Type"
						onChange={handleTypeChange}
					>
						<option value="adult">Adult</option>
						<option value="child">Child</option>
					</SelectInputField>
					<Inline alignItems="center" gap="1" padding="1">
						<Icons.Info color="muted" fontSize="xs" />{" "}
						<Text fontSize="xs" color="muted">
							Type cannot be changes once the tourist is created.
						</Text>
					</Inline>
				</Stack>
			</Col>
			<Col xs={12} sm={4} md={3} paddingY="2">
				<GetFieldValue<ITouristFormData["tourists"][number]["type"]>
					name={`${name}.type`}
				>
					{({ value: type }) => (
						<SelectField
							select={Select}
							placeholder={type === "adult" ? "e.g. Mr." : "e.g. Master"}
							name={`${name}.salutation`}
							label="Salutation"
							options={type === "adult" ? adultSalutations : childSalutations}
						/>
					)}
				</GetFieldValue>
			</Col>

			<Col xs={12} sm={5} md={6} paddingY="2">
				<TextInputField
					name={`${name}.name`}
					label="Name"
					required
					placeholder="e.g. Guest Name"
				/>
			</Col>
			<Col xs={12} sm={3} md={3} paddingY="2">
				<GetFieldValue<Date | undefined> name={`${name}.dob`}>
					{({ value: dob }) => {
						return (
							<TextInputField
								name={`${name}.age_during_trip`}
								label={`Age${tourist?.type === "child" ? `*` : ``}`}
								type="number"
								placeholder="e.g. 25"
								disabled={Boolean(dob)}
								min={0}
							/>
						)
					}}
				</GetFieldValue>
			</Col>

			<Col xs={12} sm={4} md={3} paddingY="2">
				<DatePickerField
					name={`${name}.dob`}
					label="Date of Birth"
					dateFormat="DD/MM/YYYY"
					placeholder="e.g. 12/01/1990"
					onChange={handleDobChange}
					max={new Date()}
				/>
			</Col>
			<Col xs={12} sm={5} md={6} paddingY="2">
				<SelectField
					select={SelectWorldCountries}
					name={`${name}.nationality`}
					label="Nationality"
					required
					placeholder="e.g. Indian"
					fetchOnMount
					cacheKey={`tourists-nationality-${tripId}`}
					labelKey="nationality"
					optionRenderer={({ option }) => <Text>{option.name}</Text>}
				/>
			</Col>

			<Col xs={12} sm={7} md={6} paddingY="2">
				<PhoneInputField
					name={`${name}.phone_numbers`}
					label="Phone Number(s)"
					multi
					required
					fullWidth
				/>
			</Col>
			<Col xs={12} sm={5} md={6} paddingY="2">
				<GetFieldValue name={`${name}.email`}>
					{({ value: email }) => (
						<Component initialState={!!email}>
							{({ state, setState }) => (
								<>
									{state || !!email ? (
										<TextInputField
											name={`${name}.email`}
											label="Email"
											type="email"
											placeholder="e.g. tourist@example.com"
										/>
									) : (
										<Stack gap="1">
											<Box>&nbsp;</Box>
											<Box>
												<Button
													onClick={() => setState(true)}
													title="Click to add Email Address"
												>
													<Icons.Mail />
												</Button>
											</Box>
										</Stack>
									)}
								</>
							)}
						</Component>
					)}
				</GetFieldValue>
			</Col>
		</Grid>
	)
}

function TouristDocumentsList({
	name,
	tripId,
	tourist,
}: {
	name: string
	tripId: number
	tourist: ITouristFormData["tourists"][number]
}) {
	const xhr = useXHR()
	const { value: showDocs } = useFieldValue<boolean>(`${name}.ui.showDocuments`)
	if (!showDocs) return null
	return (
		<FieldArray<ITouristFormData["tourists"][number]["documents"][number]>
			name={`${name}.documents`}
		>
			{({ fields: docFields }) => (
				<Stack gap="4">
					<Inline
						alignItems="center"
						justifyContent="between"
						padding="3"
						backgroundColor="inset"
					>
						<Inline gap="2" alignItems="center">
							<Icons.DocumentText />
							<Heading as="h3" fontSize={"md"}>
								Tourist Documents
							</Heading>
						</Inline>
						<Badge primary>{docFields.length}</Badge>
					</Inline>
					<Stack
						gap="4"
						paddingX="4"
						paddingY="4"
						justifyContent="start"
						alignItems="start"
					>
						{docFields.map((docName, docIdx) => (
							<DocumentRow
								key={docName}
								tourist={tourist}
								docName={docName}
								tripId={tripId}
								onRemove={async ({
									url,
									documentId,
									touristId,
								}: TRemoveDocumentPayload) => {
									if (url) {
										await deleteDocument({
											url,
											documentId,
											touristId,
											xhr,
										}).then(() => {
											showSnackbar("File deleted successfully!")
										})
									}
									docFields.remove(docIdx)
								}}
							/>
						))}
						<Button
							level="tertiary"
							size="sm"
							onClick={() => {
								docFields.push({
									id: null,
									uid: "",
									description: undefined,
									document_type: null,
								})
							}}
						>
							<Icons.Plus /> Add another document
						</Button>
						<Box>
							<Alert status="info">
								By uploading these documents, you confirm that they have been
								received from the respective traveler(s) with their consent for
								travel processing only. Sembark acts only as a technology
								platform and is not responsible for the authenticity, accuracy,
								or use of these documents. All uploaded documents will be
								automatically deleted 6 months from the date of upload. Please
								download and securely retain copies if required for future use.
							</Alert>
						</Box>
					</Stack>
				</Stack>
			)}
		</FieldArray>
	)
}

function DocumentRow({
	docName,
	tripId,
	onRemove,
	tourist,
}: {
	docName: string
	tripId: number
	onRemove: (arg: TRemoveDocumentPayload) => void
	tourist: ITouristFormData["tourists"][number]
}) {
	const form = useForm()
	const { value: document } = useFieldValue<TTouristDocument>(`${docName}`)
	const isFilePresent = !!document.media_url
	return (
		<Grid noGutters gap="2">
			<Col xs={5} sm={5} md={3} lg={3} xl={3}>
				<SelectField
					disabled={isFilePresent}
					select={SelectTouristDocumentType}
					fetchOnMount
					name={`${docName}.document_type`}
					placeholder="Select"
					cacheKey={`trip-document-type-${tripId}`}
					tripId={tripId}
					tripDocumentsOnly
					onChange={(value) => {
						form.change(`${docName}.document_type` as never, value as never)
					}}
				/>
			</Col>
			<Col xs={5} sm={5} md={3} lg={3} xl={3}>
				<TextInputField name={`${docName}.uid`} placeholder="Document ID" />
			</Col>
			<Col xs={5} sm={5} md={3} lg={3} xl={3}>
				<TextInputField
					name={`${docName}.description`}
					placeholder="Description (optional)"
				/>
			</Col>
			<Col xs="auto" sm="auto" md="auto" lg="auto" xl="auto">
				<GetFieldValue<TTouristDocumentType> name={`${docName}.document_type`}>
					{({ value: type }) =>
						type ? (
							<DocumentUploaderDialog
								name={docName}
								docName={type.name}
								url={document.media_url}
								isFilePresent={isFilePresent}
								tourist={tourist}
								document={document}
							/>
						) : (
							<Button disabled level="tertiary">
								<Icons.Upload />
								Upload
							</Button>
						)
					}
				</GetFieldValue>
			</Col>
			<Col xs="auto" sm="auto">
				{isFilePresent ? (
					<DocumentActions
						onDelete={() =>
							onRemove({
								url: document.media_url,
								touristId: tourist.id,
								documentId: document.id,
							})
						}
						tripId={tripId}
						docName={docName}
						tourist={tourist}
						document={document}
					/>
				) : (
					<Button
						size="base"
						level="tertiary"
						onClick={() =>
							onRemove({
								url: document.media_url,
								touristId: tourist.id,
								documentId: document.id,
							})
						}
					>
						<Icons.Cancel />
					</Button>
				)}
			</Col>
		</Grid>
	)
}

function DocumentActions({
	tripId,
	docName,
	onDelete,
	tourist,
	document,
}: {
	tripId: number
	docName: string
	onDelete: () => void
	tourist: ITouristFormData["tourists"][number]
	document: TTouristDocument
}) {
	return (
		<Dropdown alignRight>
			<Dropdown.ToggleButton>
				<Icons.DotsVertical />
			</Dropdown.ToggleButton>
			<Dropdown.Menu>
				<EditDocument
					tripId={tripId}
					docName={docName}
					tourist={tourist}
					document={document}
				>
					{({ openEditMode }) => (
						<Dropdown.MenuItem onClick={openEditMode}>
							<Icons.Pencil /> Edit Details
						</Dropdown.MenuItem>
					)}
				</EditDocument>
				<Divider marginY="0" />
				<Dropdown.MenuItem color="danger" onClick={onDelete}>
					<Icons.Trash /> Delete
				</Dropdown.MenuItem>
			</Dropdown.Menu>
		</Dropdown>
	)
}

type UploadDocumentType = {
	url?: string
	document_file?: File
	removed_file?: string
}

function DocumentUploaderDialog({
	docName,
	name,
	url,
	isFilePresent,
	tourist,
	document: touristDocument,
}: {
	docName: string
	name: string
	url?: string
	isFilePresent: boolean
	tourist: ITouristFormData["tourists"][number]
	document: TTouristDocument
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const form = useForm()
	const inputRef = useRef<HTMLInputElement>(null)
	const removeFile = async (url?: string) => {
		if (url)
			await deleteDocument({
				url,
				xhr,
				documentId: touristDocument.id,
				touristId: tourist.id,
			})
		form.change(`${name}.media_url` as never, null)
	}

	async function triggerFileInput(url: string) {
		inputRef.current?.click()
		setTimeout(() => {
			const input = document.getElementById(
				`file-input-${name}`
			) as HTMLInputElement | null
			if (input) input.click()
		}, 0)
	}

	const initialValues: UploadDocumentType = {
		url,
		document_file: undefined,
		removed_file: undefined,
	}

	return (
		<>
			<Col style={{ padding: "0px" }}>
				<Button
					level="secondary"
					status="primary"
					onClick={openDialog}
					title={isFilePresent ? "Preview" : "Upload"}
				>
					{isFilePresent ? <Icons.ArrowExpand /> : <Icons.Upload />}
					{isFilePresent ? "Uploaded" : "Upload"}
				</Button>
			</Col>
			<Dialog sm open={isOpen} onClose={closeDialog} closeOnEscape>
				<Form<UploadDocumentType>
					validate={validateFormValues(
						Validator.object().shape({
							document_file: Validator.mixed().required(
								`Please select a ${docName} image to upload.`
							),
						})
					)}
					initialValues={
						url
							? initialValues
							: {
									url: undefined,
									document_file: undefined,
									removed_file: undefined,
								}
					}
					onSubmit={withServerErrors(async (values) => {
						if (values.removed_file) {
							await removeFile(values.removed_file)
						}
						if (values.document_file) {
							const response = await TouristXHR(xhr).preUploadDocument(
								values.document_file
							)
							if (response.media_url) {
								form.change(`${name}.media_url`, response.media_url)
							}
						} else {
							alert(
								`Please select a valid file to upload as the ${docName}. The document file is required.`
							)
						}
						closeDialog()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, form, submitting }) => (
						<form
							noValidate
							onSubmit={handleSubmit}
							encType="multipart/form-data"
						>
							<Dialog.Header>
								<Dialog.Title>
									<Inline gap="4" alignItems="center">
										<Text>Upload {docName}</Text>
									</Inline>
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<Stack gap="2">
									<GetFieldValue<File> name="document_file">
										{({ value: file }) => (
											<GetFieldValue<string> name="url">
												{({ value: url }) =>
													file || url ? (
														<Card
															bgColor="subtle"
															paddingX="4"
															paddingTop="2"
															paddingBottom="4"
														>
															<Stack gap="2">
																<Inline
																	paddingY="2"
																	justifyContent="between"
																	gap="2"
																>
																	<FileInfo file={file} url={url} />
																	<Inline
																		justifyContent="between"
																		alignItems="center"
																		gap="4"
																	>
																		<Button
																			level="secondary"
																			status="primary"
																			onClick={() => {
																				if (url) {
																					form.change("removed_file", url)
																				}
																				form.change("document_file", undefined)
																				form.change("url", undefined)
																				triggerFileInput(url)
																			}}
																		>
																			<Icons.Upload />{" "}
																			{file || url ? "Update" : "Upload"}
																		</Button>
																	</Inline>
																</Inline>
																<FilePreview url={url} file={file} />
															</Stack>
														</Card>
													) : (
														<DocumentFileInputField
															ref={inputRef}
															name={"document_file"}
															id={`file-input-${name}`}
														/>
													)
												}
											</GetFieldValue>
										)}
									</GetFieldValue>
									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									{submitting ? "Saving..." : "Save"}
								</Button>
								<Button
									onClick={closeDialog}
									level="tertiary"
									disabled={!!submitting}
								>
									Close
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

export function FileInfo({ file, url }: { file?: File; url?: string }) {
	const name = file?.name || url?.split("/").at(-1)
	return (
		<Stack width="2/3" gap="1">
			<Text wordBreak="all">{name}</Text>
			{file ? <Text>{formatFileSize(file.size)}</Text> : null}
			{url ? (
				<Badge success style={{ width: "fit-content" }}>
					Already Uploaded
				</Badge>
			) : null}
		</Stack>
	)
}

export function FilePreview({ file, url }: { file?: File; url?: string }) {
	const [previewURL, setPreviewURL] = useState<string | undefined>(undefined)
	// Determine URL
	useEffect(() => {
		if (file) {
			const objectURL = globalThis.URL.createObjectURL(file)
			setPreviewURL(objectURL)
			return () => globalThis.URL.revokeObjectURL(objectURL)
		} else if (url) {
			setPreviewURL(url)
		}
	}, [file, url])

	if (!previewURL) return null

	const isPDF =
		file?.type === "application/pdf" || /\.pdf(\?|#|$)/i.test(previewURL)
	const name = file?.name || previewURL.split("/").at(-1)
	return (
		<Inline gap="4" alignItems="center" width="full">
			<Box width="full">
				{isPDF ? (
					<Box
						as="iframe"
						title={name}
						src={previewURL}
						width="full"
						rounded="md"
						style={{ border: "none", height: "400px" }}
					/>
				) : (
					<Box
						as="img"
						rounded="md"
						src={previewURL}
						alt={name}
						style={{
							width: "100%",
							maxHeight: "70vh",
							objectFit: "contain",
							border: "none",
						}}
					/>
				)}
			</Box>
		</Inline>
	)
}

function EditDocument({
	children,
	docName,
	tripId,
	tourist,
	document,
}: {
	children: (props: { openEditMode: () => void }) => React.ReactNode
	docName: string
	tripId: number
	tourist: ITouristFormData["tourists"][number]
	document: TTouristDocument
}) {
	const form = useForm()
	const { value: initialValues } = useFieldValue<TTouristDocument>(`${docName}`)
	const [isOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()
	return (
		<>
			{children({ openEditMode: () => openDialog() })}
			<Dialog open={isOpen} onClose={closeDialog} title="Edit Details" sm>
				<Form<TTouristDocument>
					initialValues={{
						...initialValues,
						file: undefined,
						removed_file: undefined,
					}}
					validate={validateFormValues(
						Validator.object().shape({
							document_type: Validator.mixed().required(
								"Document type is required"
							),
							uid: Validator.string().required("Document ID is required"),
							description: Validator.string().nullable(),
						})
					)}
					onSubmit={withServerErrors(async ({ file, ...rest }) => {
						if (rest.removed_file)
							await deleteDocument({
								xhr,
								url: rest.removed_file,
								documentId: document.id,
								touristId: tourist.id,
							})

						const res = file
							? await TouristXHR(xhr).preUploadDocument(file)
							: null
						const data = {
							...rest,
							media_url: res?.media_url ?? rest.media_url,
						}
						form.change(`${docName}`, data)
						closeDialog()
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, form, handleSubmit }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Grid gap="4" noGutters justifyContent="between">
									<Col sm={6} md={6} lg={6} xl={6}>
										<SelectField
											select={SelectTouristDocumentType}
											fetchOnMount
											label="Document"
											name={`document_type`}
											placeholder="Select"
											cacheKey={`trip-document-type-${tripId}`}
											tripId={tripId}
											onChange={(value) => {
												form.change(`document_type` as never, value as never)
											}}
										/>
									</Col>
									<Col sm={5} md={5} lg={5} xl={5}>
										<TextInputField
											label="Document ID"
											name={`uid`}
											placeholder="Document ID"
										/>
									</Col>
									<Col sm={12} md={12} lg={12} xl={12}>
										<TextInputField
											name={`description`}
											label="Description (Optional)"
											placeholder="Description (optional)"
										/>
									</Col>
									<Col sm={12} md={12} lg={12} xl={12}>
										<FileCard />
									</Col>
								</Grid>
							</Dialog.Body>
							<Dialog.Footer>
								<Button
									disabled={submitting}
									type="submit"
									status="primary"
									level="primary"
								>
									{submitting ? "Saving..." : "Save"}
								</Button>
								<Button
									disabled={submitting}
									onClick={() => closeDialog()}
									level="tertiary"
								>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function FileCard() {
	const { value: type } = useFieldValue<IDocType>("document_type")
	const { value: url } = useFieldValue<string>("media_url")
	const { value: file } = useFieldValue<File>("file")
	if (!type) return null
	const isFilePresent = Boolean(url || file)
	return type ? (
		<UploadFileCard file={file} url={url} isFilePresent={isFilePresent} />
	) : null
}

const formatFileSize = (size: number) => {
	const KB = 1024
	const MB = KB * 1024
	if (size < KB) {
		return `${size} B`
	} else if (size < MB) {
		return `${(size / KB).toFixed(1)} KB`
	} else {
		return `${(size / MB).toFixed(2)} MB`
	}
}

function UploadFileCard({
	url,
	file,
	isFilePresent,
}: {
	url: string
	file: File
	isFilePresent: boolean
}) {
	const form = useForm()

	const removeFile = async () => {
		form.change(`file` as never, null)
		form.change(`media_url` as never, null)
		form.change(`removed_file`, url)
	}

	async function triggerFileInput() {
		await removeFile()
		setTimeout(() => {
			const input = document.getElementById(
				`file-input-edit-doc`
			) as HTMLInputElement | null
			if (input) input.click()
		}, 0)
	}

	return (
		<Box>
			{isFilePresent ? (
				<Card bgColor="subtle" paddingX="4" paddingTop="2" paddingBottom="4">
					<Inline paddingY="2" justifyContent="between">
						<FileInfo file={file} url={url} />
						<Inline justifyContent="between" alignItems="center" gap="4">
							<Button
								level="secondary"
								status="primary"
								onClick={triggerFileInput}
							>
								<Icons.Upload /> {isFilePresent ? "Update" : "Upload"}
							</Button>
							<Button inline onClick={removeFile}>
								<Icons.Cancel />
							</Button>
						</Inline>
					</Inline>
					<FilePreview file={file} url={url} />
				</Card>
			) : (
				<DocumentFileInputField id="file-input-edit-doc" name="file" />
			)}
		</Box>
	)
}
